// Blueprint covers for the "Need a spark?" rail. The rail deliberately does
// NOT show finished-looking example posts: an example invites comparison with
// a real post and always loses (generic, or fake-authentic). A BLUEPRINT makes
// the honest promise instead — this is the recipe, and Taka crafts the real
// thing for YOUR brand when you tap (Matan, 2026-07-05). Pure client-side SVG:
// instant paint, brand-agnostic, zero renders, works identically in both themes
// (fixed cyanotype colors on purpose — the cover is content, not chrome).

const INK = '#EAF0FF'
const FAINT = 'rgba(234,240,255,0.55)'
const LINE = 'rgba(234,240,255,0.8)'
const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
const DASH = '3 2.4'

/** Stable recipe number per catalog format (order = server catalog order). */
const RECIPE_NO: Record<string, number> = {
  receipts: 1,
  'behind-the-scenes': 2,
  'customer-words': 3,
  'how-we-make-it': 4,
  'myth-vs-fact': 5,
  'faq-answered': 6,
  'this-week-only': 7,
  'sensory-reel': 8,
  'before-after': 9,
  'hot-take': 10,
  // The how-to POST TYPE (playbook/product/post-types/how-to.md), added 2026-09-06;
  // numbers stay stable, so it takes the next one rather than reordering the rail.
  'teach-one-thing': 11,
  // The hook video POST TYPE (playbook/product/post-types/hook-video.md), added 2026-09-15.
  'hook-reel': 12,
  // The product-reveal POST TYPE (playbook/product/post-types/product-reveal.md), added 2026-10-13.
  'product-reveal': 13,
}

function Annotation({
  x,
  y,
  children,
  anchor = 'middle',
}: {
  x: number
  y: number
  children: string
  anchor?: 'start' | 'middle' | 'end'
}) {
  return (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      fontFamily={MONO}
      fontSize={7}
      letterSpacing={0.8}
      fill={FAINT}
    >
      {children}
    </text>
  )
}

/** Per-format schematic, drawn in a 200x200 blueprint sheet. */
function Schematic({ formatId }: { formatId: string }) {
  switch (formatId) {
    case 'receipts':
      return (
        <>
          <text
            x={100}
            y={104}
            textAnchor="middle"
            fontFamily={MONO}
            fontSize={54}
            fontWeight={700}
            fill="none"
            stroke={INK}
            strokeWidth={1.1}
          >
            128
          </text>
          <line x1={62} y1={116} x2={138} y2={116} stroke={LINE} strokeWidth={1.4} />
          <Annotation x={100} y={132}>
            YOUR NUMBER, THIS WEEK
          </Annotation>
          <Annotation x={100} y={158}>
            fig.1: count what happened
          </Annotation>
        </>
      )
    case 'behind-the-scenes':
      return (
        <>
          <rect
            x={52}
            y={52}
            width={96}
            height={78}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <line x1={52} y1={52} x2={148} y2={130} stroke={FAINT} strokeWidth={0.5} />
          <line x1={148} y1={52} x2={52} y2={130} stroke={FAINT} strokeWidth={0.5} />
          <circle cx={82} cy={76} r={7} fill="none" stroke={LINE} strokeWidth={0.9} />
          <path
            d="M60 122 L92 92 L112 110 L126 98 L140 122"
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
          />
          <Annotation x={100} y={146}>
            YOUR MESSY MIDDLE HERE
          </Annotation>
          <Annotation x={100} y={160}>
            fig.1: no staging allowed
          </Annotation>
        </>
      )
    case 'customer-words':
      // The testimonial post: five stars over the customer's own chat bubbles, cut out of
      // their screenshot (playbook/product/post-types/testimonial.md).
      return (
        <>
          <g fill={INK} transform="translate(64 52)">
            {[0, 1, 2, 3, 4].map(i => (
              <path
                key={i}
                transform={`translate(${i * 15} 0) scale(0.55)`}
                d="M12 1.5l3 6.6 7.2.8-5.3 4.9 1.4 7.1L12 17.4l-6.3 3.5 1.4-7.1L1.8 8.9 9 8.1z"
              />
            ))}
          </g>
          <rect
            x={48}
            y={78}
            width={92}
            height={26}
            rx={8}
            fill="none"
            stroke={LINE}
            strokeWidth={1}
          />
          <line x1={58} y1={88} x2={128} y2={88} stroke={LINE} strokeWidth={1} />
          <line x1={58} y1={96} x2={104} y2={96} stroke={LINE} strokeWidth={1} />
          <rect
            x={60}
            y={112}
            width={92}
            height={26}
            rx={8}
            fill="none"
            stroke={LINE}
            strokeWidth={1}
          />
          <line x1={70} y1={122} x2={140} y2={122} stroke={LINE} strokeWidth={1} />
          <line x1={70} y1={130} x2={112} y2={130} stroke={LINE} strokeWidth={1} />
          <Annotation x={100} y={160}>
            fig.1: their screenshot, their words
          </Annotation>
        </>
      )
    case 'how-we-make-it':
      return (
        <>
          <rect
            x={46}
            y={62}
            width={44}
            height={56}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <rect
            x={78}
            y={70}
            width={44}
            height={56}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <rect x={110} y={78} width={44} height={56} fill="none" stroke={LINE} strokeWidth={0.9} />
          <text x={68} y={94} textAnchor="middle" fontFamily={MONO} fontSize={12} fill={INK}>
            1
          </text>
          <text x={100} y={102} textAnchor="middle" fontFamily={MONO} fontSize={12} fill={INK}>
            2
          </text>
          <text x={132} y={110} textAnchor="middle" fontFamily={MONO} fontSize={12} fill={INK}>
            3
          </text>
          <path d="M158 106 h14 m-5 -4 l5 4 l-5 4" fill="none" stroke={LINE} strokeWidth={1} />
          <Annotation x={100} y={156}>
            ONE STEP PER SLIDE
          </Annotation>
        </>
      )
    case 'teach-one-thing':
      // The tutorial the READER does: a "Step 01" kicker box over a verb line, the
      // next steps stacked behind, and the save bookmark the whole deck is for.
      return (
        <>
          <rect x={58} y={54} width={84} height={98} fill="none" stroke={LINE} strokeWidth={0.9} />
          <rect x={66} y={62} width={34} height={12} fill="none" stroke={INK} strokeWidth={0.9} />
          <text
            x={83}
            y={71}
            textAnchor="middle"
            fontFamily={MONO}
            fontSize={7}
            letterSpacing={0.8}
            fill={INK}
          >
            STEP 01
          </text>
          <line x1={66} y1={90} x2={132} y2={90} stroke={INK} strokeWidth={1.4} />
          <line x1={66} y1={100} x2={118} y2={100} stroke={LINE} strokeWidth={1.1} />
          <line x1={66} y1={116} x2={110} y2={116} stroke={FAINT} strokeWidth={0.9} />
          <line x1={66} y1={124} x2={122} y2={124} stroke={FAINT} strokeWidth={0.9} />
          <rect
            x={146}
            y={62}
            width={22}
            height={98}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <text x={157} y={78} textAnchor="middle" fontFamily={MONO} fontSize={7} fill={FAINT}>
            02
          </text>
          <text x={157} y={94} textAnchor="middle" fontFamily={MONO} fontSize={7} fill={FAINT}>
            03
          </text>
          <path d="M126 136 v14 l5 -4 l5 4 v-14 z" fill="none" stroke={INK} strokeWidth={1} />
          <Annotation x={100} y={170}>
            HOW TO DO IT, ONE STEP PER SLIDE
          </Annotation>
          <Annotation x={100} y={184}>
            fig.1: save it, do it later
          </Annotation>
        </>
      )
    case 'product-reveal':
      // The torn-paper reveal: one photo under a paper sheet whose window opens wider on
      // every swipe; the four dots count the swipes, the line under the window is the hook.
      return (
        <>
          <rect x={52} y={44} width={96} height={120} fill="none" stroke={LINE} strokeWidth={0.9} />
          <path
            d="M52 86 l9 -4 l8 5 l10 -6 l9 4 l10 -5 l9 6 l10 -4 l9 5 l10 -6 l12 4 v30 l-10 5 l-9 -4 l-10 6 l-9 -5 l-10 4 l-9 -6 l-10 5 l-9 -4 l-10 6 l-10 -3 z"
            fill="none"
            stroke={INK}
            strokeWidth={1.1}
          />
          <line x1={66} y1={104} x2={134} y2={104} stroke={FAINT} strokeWidth={0.5} />
          <circle cx={100} cy={104} r={9} fill="none" stroke={FAINT} strokeWidth={0.6} />
          <line x1={72} y1={140} x2={128} y2={140} stroke={INK} strokeWidth={1.4} />
          <line x1={84} y1={149} x2={116} y2={149} stroke={LINE} strokeWidth={0.9} />
          <circle cx={91} cy={158} r={1.6} fill={INK} />
          <circle cx={97} cy={158} r={1.6} fill="none" stroke={FAINT} strokeWidth={0.6} />
          <circle cx={103} cy={158} r={1.6} fill="none" stroke={FAINT} strokeWidth={0.6} />
          <circle cx={109} cy={158} r={1.6} fill="none" stroke={FAINT} strokeWidth={0.6} />
          <Annotation x={100} y={178}>
            HOLD THE DOTS, THE PAPER TEARS OPEN
          </Annotation>
          <Annotation x={100} y={190}>
            fig.1: the reveal does the selling
          </Annotation>
        </>
      )
    case 'myth-vs-fact':
      return (
        <>
          <rect
            x={40}
            y={66}
            width={54}
            height={64}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <rect x={106} y={66} width={54} height={64} fill="none" stroke={LINE} strokeWidth={0.9} />
          <text
            x={67}
            y={92}
            textAnchor="middle"
            fontFamily={MONO}
            fontSize={9}
            letterSpacing={1}
            fill={INK}
          >
            MYTH
          </text>
          <line x1={52} y1={89} x2={82} y2={89} stroke={INK} strokeWidth={1.1} />
          <text
            x={133}
            y={92}
            textAnchor="middle"
            fontFamily={MONO}
            fontSize={9}
            letterSpacing={1}
            fill={INK}
          >
            FACT
          </text>
          <path d="M126 108 l5 6 l10 -12" fill="none" stroke={LINE} strokeWidth={1.3} />
          <path d="M60 104 l14 14 m0 -14 l-14 14" fill="none" stroke={FAINT} strokeWidth={1} />
          <Annotation x={100} y={156}>
            CORRECT THE RECORD
          </Annotation>
        </>
      )
    case 'faq-answered':
      return (
        <>
          <rect
            x={44}
            y={58}
            width={50}
            height={50}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <text x={69} y={92} textAnchor="middle" fontFamily={MONO} fontSize={26} fill={INK}>
            ?
          </text>
          <path d="M100 84 h16 m-5 -4 l5 4 l-5 4" fill="none" stroke={LINE} strokeWidth={1} />
          <rect x={124} y={70} width={50} height={50} fill="none" stroke={LINE} strokeWidth={0.9} />
          <text x={149} y={102} textAnchor="middle" fontFamily={MONO} fontSize={20} fill={INK}>
            A.
          </text>
          <Annotation x={100} y={144}>
            THE QUESTION YOU ALWAYS GET
          </Annotation>
          <Annotation x={100} y={158}>
            fig.1: answer it once, in public
          </Annotation>
        </>
      )
    case 'this-week-only':
      return (
        <>
          <rect
            x={68}
            y={52}
            width={64}
            height={16}
            rx={8}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
          />
          <text
            x={100}
            y={63}
            textAnchor="middle"
            fontFamily={MONO}
            fontSize={7.5}
            letterSpacing={1}
            fill={INK}
          >
            THIS WEEK
          </text>
          <line x1={52} y1={90} x2={148} y2={90} stroke={LINE} strokeWidth={1.2} />
          <line x1={52} y1={102} x2={126} y2={102} stroke={LINE} strokeWidth={1.2} />
          <circle cx={100} cy={132} r={13} fill="none" stroke={LINE} strokeWidth={0.9} />
          <path d="M100 132 V123 M100 132 L107 136" fill="none" stroke={LINE} strokeWidth={1} />
          <Annotation x={100} y={160}>
            ENDS SUNDAY. SAY SO
          </Annotation>
        </>
      )
    case 'hook-reel':
      // The hook video + value caption: a looping phone frame, ONE boxed line held
      // upper-center with the arrow at its end, and the value stacked under the frame
      // where the caption lives.
      return (
        <>
          <rect
            x={70}
            y={40}
            width={60}
            height={104}
            rx={7}
            fill="none"
            stroke={LINE}
            strokeWidth={1}
            strokeDasharray={DASH}
          />
          <rect
            x={77}
            y={70}
            width={46}
            height={22}
            rx={4}
            fill="none"
            stroke={INK}
            strokeWidth={1.1}
          />
          <line x1={83} y1={78} x2={113} y2={78} stroke={INK} strokeWidth={1.3} />
          <line x1={83} y1={85} x2={104} y2={85} stroke={INK} strokeWidth={1.3} />
          <path d="M111 82 v6 M108 85 l3 3 l3 -3" fill="none" stroke={INK} strokeWidth={1} />
          <path d="M60 92 a40 40 0 0 1 0 -44" fill="none" stroke={FAINT} strokeWidth={0.9} />
          <path d="M60 48 l-3 4 l4 1" fill="none" stroke={FAINT} strokeWidth={0.9} />
          <line x1={72} y1={154} x2={128} y2={154} stroke={LINE} strokeWidth={1.1} />
          <line x1={72} y1={162} x2={118} y2={162} stroke={FAINT} strokeWidth={0.9} />
          <line x1={72} y1={170} x2={124} y2={170} stroke={FAINT} strokeWidth={0.9} />
          <Annotation x={100} y={186}>
            ONE LINE ON A LOOP. THE VALUE BELOW
          </Annotation>
        </>
      )
    case 'sensory-reel':
      return (
        <>
          <rect
            x={76}
            y={44}
            width={48}
            height={92}
            rx={7}
            fill="none"
            stroke={LINE}
            strokeWidth={1}
            strokeDasharray={DASH}
          />
          <path d="M94 82 l16 8 l-16 8 z" fill="none" stroke={INK} strokeWidth={1.1} />
          <path
            d="M132 78 a10 10 0 0 1 0 24 M138 72 a18 18 0 0 1 0 36"
            fill="none"
            stroke={FAINT}
            strokeWidth={0.9}
          />
          <text x={100} y={150} textAnchor="middle" fontFamily={MONO} fontSize={8} fill={INK}>
            0:05
          </text>
          <Annotation x={100} y={164}>
            CLOSE-UP. SOUND ON.
          </Annotation>
        </>
      )
    case 'before-after':
      return (
        <>
          <rect x={40} y={66} width={50} height={62} fill="none" stroke={LINE} strokeWidth={0.9} />
          <path
            d="M42 126 L88 68 M42 108 L74 68 M42 90 L58 68 M58 126 L88 88 M74 126 L88 108"
            stroke={FAINT}
            strokeWidth={0.5}
          />
          <path d="M94 97 h12 m-4 -4 l4 4 l-4 4" fill="none" stroke={LINE} strokeWidth={1} />
          <rect x={110} y={66} width={50} height={62} fill="none" stroke={LINE} strokeWidth={0.9} />
          <Annotation x={65} y={142}>
            BEFORE
          </Annotation>
          <Annotation x={135} y={142}>
            AFTER
          </Annotation>
          <Annotation x={100} y={162}>
            THE SWIPE IS THE STORY
          </Annotation>
        </>
      )
    case 'hot-take':
      return (
        <>
          <rect
            x={48}
            y={58}
            width={104}
            height={64}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <line x1={58} y1={76} x2={142} y2={76} stroke={LINE} strokeWidth={1.2} />
          <line x1={58} y1={90} x2={128} y2={90} stroke={LINE} strokeWidth={1.2} />
          <line x1={58} y1={104} x2={110} y2={104} stroke={INK} strokeWidth={1.6} />
          <path
            d="M58 110 q6 3 12 0 t12 0 t12 0 t12 0"
            fill="none"
            stroke={FAINT}
            strokeWidth={0.8}
          />
          <Annotation x={100} y={140}>
            SAY THE QUIET PART
          </Annotation>
          <Annotation x={100} y={156}>
            fig.1: a real point of view
          </Annotation>
        </>
      )
    default:
      return (
        <>
          <rect
            x={52}
            y={60}
            width={96}
            height={70}
            fill="none"
            stroke={LINE}
            strokeWidth={0.9}
            strokeDasharray={DASH}
          />
          <Annotation x={100} y={152}>
            TAKA DRAFTS THIS FOR YOU
          </Annotation>
        </>
      )
  }
}

/**
 * The full blueprint sheet: cyanotype field, drafting grid, registration
 * marks, recipe number, format schematic, and the promise line.
 */
export function SparkBlueprint({ formatId }: { formatId: string }) {
  const no = RECIPE_NO[formatId]
  const gridId = `bp-grid-${formatId}`
  return (
    // span (block), not div: the cover mounts inside StreamRail's <button>,
    // which only allows phrasing content — the card body keeps the same rule.
    <span
      className="relative block aspect-square w-full overflow-hidden"
      style={{ background: 'linear-gradient(150deg, #17339B 0%, #0C1F66 100%)' }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full">
        <defs>
          <pattern id={gridId} width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M12 0H0V12" fill="none" stroke="rgba(234,240,255,0.10)" strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="200" height="200" fill={`url(#${gridId})`} />
        {/* Sheet frame + corner registration marks: the "this is a drawing" cues. */}
        <rect
          x={10}
          y={10}
          width={180}
          height={180}
          fill="none"
          stroke="rgba(234,240,255,0.35)"
          strokeWidth={0.7}
          strokeDasharray={DASH}
        />
        <path
          d="M10 22 V10 H22 M178 10 H190 V22 M190 178 V190 H178 M22 190 H10 V178"
          fill="none"
          stroke={LINE}
          strokeWidth={1}
        />
        {/* A server format this client doesn't know yet gets the plain "RECIPE"
            stamp — "Nº 00" would read as a bug, not a graceful default. */}
        <text x={20} y={30} fontFamily={MONO} fontSize={7.5} letterSpacing={1.2} fill={LINE}>
          {no ? `RECIPE Nº ${String(no).padStart(2, '0')}` : 'RECIPE'}
        </text>
        <text
          x={180}
          y={30}
          textAnchor="end"
          fontFamily={MONO}
          fontSize={7.5}
          letterSpacing={1.2}
          fill={FAINT}
        >
          PLAN
        </text>
        <Schematic formatId={formatId} />
        <line x1={20} y1={176} x2={64} y2={176} stroke={FAINT} strokeWidth={0.6} />
        <text x={20} y={186} fontFamily={MONO} fontSize={6.5} letterSpacing={0.9} fill={FAINT}>
          TAKA CRAFTS THIS FOR YOUR BRAND
        </text>
      </svg>
    </span>
  )
}
